import { createSlice } from '@reduxjs/toolkit'
import * as Yup from 'yup'

import { initYup } from "utils/helpers";
initYup();

type AddItemPayload<K extends keyof HeatModeForm> = {
  name: K
  value: HeatModeForm[K]
}

export const heatModeSchema = Yup.object({
  departement: Yup.string().required(),
  stationMeteo: Yup.string().required(),
  altitude: Yup.number().integer().required(),
  placesaid: Yup.string(),
  seaside: Yup.boolean().required(),
  tbase: Yup.number(),
  tbaseModified: Yup.boolean(),
  mitoyennete: Yup.number().integer().required(),
  level: Yup.number().integer().required(),
  stationsList: Yup.array(),
  departmentsList: Yup.array(),
  zones: Yup.array()
    .of(
      Yup.object({
        surface: Yup.number().required(),
        hauteur: Yup.number().required(),
        emetteur: Yup.number().required(),
        temperatureNeeded: Yup.boolean(),
        temperature: Yup.mixed().when('temperatureNeeded', {
          is: true,
          then: () =>
            Yup.number()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
          otherwise: () => Yup.mixed(),
        }),
      }),
    )
    .min(1, 'Au moins une zone est requise'),

  deperditions: Yup.lazy((value) => {
    if (value !== undefined) {
      switch (value.deperditionMethod) {
        case '0': // Connues
          return Yup.object({
            deperditionMethod: Yup.string().required(),
            temperature: Yup.number().required(),
            deperditions: Yup.number()
              .integer()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
          })

        case '1': // DPE
          return Yup.object({
            deperditionMethod: Yup.string().required(),
            temperature: Yup.number().required(),
            dpe: Yup.number()
              .integer()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
          })

        case '2': // Invoice
          return Yup.object({
            deperditionMethod: Yup.string().required(),
            temperature: Yup.number().required(),
            annualBilling: Yup.number()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
            actualSystem: Yup.string().required(),
            nrgPrice: Yup.number()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
            withECS: Yup.string().required(),
            nbPeoples: Yup.mixed().when('withECS', {
              is: '1',
              then: () => Yup.string().required('Nombre de personnes requis'),
              otherwise: () => Yup.mixed(),
            }),
            systemAge: Yup.string().required(),
            dayTemp: Yup.string().required(),
            nightTemp: Yup.string().required(),
          })

        case '3': // Coefficient
          return Yup.object({
            deperditionMethod: Yup.string().required(),
            temperature: Yup.number().required(),
            //year: Yup.number().min(100, "ERROR_YEAR").max(2035, "ERROR_YEAR").required("ERROR_YEAR"),
            year: Yup.number()
              .min(100)
              .max(2035)
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
            insulationWork: Yup.string().required(),
            walls: Yup.mixed().when('insulationWork', {
              is: '1',
              then: () => Yup.string().required('Murs requis'),
              otherwise: () => Yup.mixed(),
            }),
            roofing: Yup.mixed().when('insulationWork', {
              is: '1',
              then: () => Yup.string().required('Toiture requise'),
              otherwise: () => Yup.mixed(),
            }),
            windows: Yup.mixed().when('insulationWork', {
              is: '1',
              then: () => Yup.string().required('Fenêtres requises'),
              otherwise: () => Yup.mixed(),
            }),
          })

        default:
          return Yup.object({
            deperditionMethod: Yup.string().required(),
          })
      }
    }

    // fallback
    return Yup.mixed().notRequired()
  }),
})

type Zone = {
  surface?: number
  hauteur: number
  emetteur: any
  emetteur_i18nCode: string
  temperatureNeeded: boolean
  temperature: string
}

export const defaultZone: Zone = {
  surface: undefined,
  hauteur: 2.5,
  emetteur: undefined,
  emetteur_i18nCode: '',
  temperatureNeeded: true,
  temperature: '',
}

export const defaultForm = {
  departement: undefined,
  departement_value: '',
  stationMeteo: undefined,
  stationMeteo_value: '',
  altitude: undefined,
  placesaid: '',
  seaside: false,
  tbase: undefined,
  tbaseModified: false,
  mitoyennete: 0,
  mitoyennete_i18nCode: '0_WALL',
  level: 0,
  level_i18nCode: '0_LEVEL',
  zones: [{ ...defaultZone }] as Zone[],
  // zones: [defaultZone],
  deperditions: {
    deperditionMethod: undefined,
    deperditionMethod_i18nCode: '',
    temperature: 19,
    deperditions: '',
    year: '',
    insulationWork: 0,
    // walls: undefined,
    walls: '',
    walls_i18nCode: '',
    // roofing: undefined,
    roofing: '',
    roofing_i18nCode: '',
    // windows: undefined,
    windows: '',
    windows_i18nCode: '',
    dpe: '',
    annualBilling: '',
    actualSystem: undefined,
    actualSystem_i18nCode: '',
    nrgPrice: '',
    nrgUnit: '',
    withECS: undefined,
    withECS_i18nCode: '',
    nbPeoples: undefined,
    systemAge: undefined,
    systemAge_i18nCode: '',
    dayTemp: '',
    nightTemp: '',
    loss: {},
  },
  stationsList: [] as ValueListItem[],
  departmentsList: [] as ValueListItem[],
}

const initialState = {
  form: { ...defaultForm },
}

export type HeatModeForm = typeof defaultForm

export const heatModeSlice = createSlice({
  name: 'heatMode',
  initialState,
  reducers: {
    initNewProject(state) {
      state.form = { ...defaultForm }
      state.form.zones = [{ ...defaultZone }]
    },
    updateMitoyennete(state, action) {
      state.form.mitoyennete = action.payload.code
      state.form.mitoyennete_i18nCode = action.payload.label
    },
    updateLevel(state, action) {
      state.form.level = action.payload.code
      state.form.level_i18nCode = action.payload.label
    },
    addZone(state) {
      state.form.zones.push({ ...defaultZone })
    },
    removeZone(state, action) {
      state.form.zones.splice(action.payload, 1)
    },
    updateTbase(state, action) {
      state.form.tbase = action.payload
      state.form.tbaseModified = false
    },
    addItem<K extends keyof HeatModeForm>(state: any, action: { payload: AddItemPayload<K> }) {
      const { name, value } = action.payload
      state.form[name] = value
    },
    load(state, action) {
      state.form = { ...action.payload }
    },
  },
})

export const { initNewProject, updateMitoyennete, updateLevel, addZone, removeZone, updateTbase, load, addItem } = heatModeSlice.actions

export default heatModeSlice.reducer
